import {
    getCoreRowModel,
    getFilteredRowModel, getSortedRowModel,
    useReactTable
} from "@tanstack/react-table";
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import AddRegistrationResultDialog from '../dialogs/addRegistrationResultDialog';
import { Badge } from '../ui/badge';
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { ChevronDownIcon } from "@radix-ui/react-icons";

export const columns = (fetchClass, formatAttempt, competitionClass, level, auth, dataLength) => {
    const isAdminOrPrivilegedUser = auth?.user?.superadmin || auth?.user?.judge;

    const baseColumns = [
        {
            id: "bunnyName",
            accessorKey: "bunny.name",
            header: ({ column }) => (
                <DatatableSortButton column={column}>Kanin</DatatableSortButton>
            ),
            cell: info => (
                <Link
                    to={`/bunny/${info.row.original.bunny.id}`}
                    className='text-blue-600 font-medium hover:underline'
                >
                    {info.row.original.bunny.name}
                </Link>
            ),
        },
        {
            accessorKey: "driver_name",
            accessorFn: (row) => row.driver?.first_name, // extract the nested first name
            header: ({ column }) => <DatatableSortButton column={column}>Förare</DatatableSortButton>,
            cell: info => {
                const driver = info.row.original.driver;
                return `${driver.first_name} ${driver.last_name}`;
            }
        },
        {
            accessorKey: "formatted_attempts",
            header: ({ column }) => <DatatableSortButton column={column}>Försök</DatatableSortButton>,
            cell: info => {
                const heightLengthResults = info.row.original.heightLengthResults || [];
                const attempts = heightLengthResults.find(
                    result => parseFloat(result.height_length) === parseFloat(level)
                ) || {};
                return formatAttempt(attempts);
            }
        },
    ];

    const columnConfig = competitionClass?.finished_at
        ? {
            accessorKey: "placing",
            header: ({ column }) => <DatatableSortButton column={column}>Plac.</DatatableSortButton>,
            cell: info => {
                const bunny = info.row.original || {}; // Safely access bunny
                const placing = info.getValue() || "Ej plac."; // Display placing or a placeholder

                return (
                    <div className='flex items-center'>
                        <span>{placing}</span>
                        {bunny.crossedOut ? (
                            <Badge className='ml-3 text-nowrap' variant='destructive'>S</Badge>
                        ) : bunny.outOfCompetition && (
                            <Badge className='ml-3 text-nowrap' variant='secondary'>UT</Badge>
                        )}
                    </div>
                );
            },
            size: 10,
        }
        : {
            accessorKey: "startNumber",
            header: ({ column }) => <DatatableSortButton column={column}>Snr.</DatatableSortButton>,
            cell: info => {
                const bunny = info.row.original || {}; // Safely access bunny
                const startNumber = info.row.original.startNumber || ''; // Safely get startNumber

                return (
                    <div className='flex items-center'>
                        <span>{startNumber}</span>
                        {bunny.crossedOut ? (
                            <Badge className='ml-3 text-nowrap' variant='destructive'>S</Badge>
                        ) : bunny.outOfCompetition && (
                            <Badge className='ml-3 text-nowrap' variant='secondary'>UT</Badge>
                        )}
                    </div>
                );
            },
            size: 10,
        };

    // Insert the determined column at the start of baseColumns
    baseColumns.splice(0, 0, columnConfig);

    // Add the "add_result" column if the user has the appropriate role
    if (isAdminOrPrivilegedUser && dataLength > 0 && !competitionClass?.finished_at) {
        baseColumns.push(
            {
                accessorKey: "add_result",
                enableHiding: false,
                header: "Lägg till resultat",
                cell: info => {
                    const registration = info.row.original;
                    const levelResult = registration.heightLengthResults?.find(
                        result => parseFloat(result.height_length) === parseFloat(level)
                    );

                    const hasResult =
                        levelResult &&
                        (levelResult.attempt1 !== null || levelResult.attempt2 !== null || levelResult.attempt3 !== null);

                    const isPlaceholder =
                        levelResult &&
                        (levelResult.attempt1 === null && levelResult.attempt2 === null && levelResult.attempt3 === null);

                    const competitionFinished = competitionClass?.finished_at; // Check if competition is finished
                    const higherLevelsAttempted = registration.heightLengthResults?.some(
                        result => parseFloat(result.height_length) > parseFloat(level)
                    );

                    if (competitionFinished || higherLevelsAttempted) {
                        return <span className='text-muted-foreground'>Resultat tillagt</span>;
                    }

                    if (isPlaceholder) {
                        return (
                            <AddRegistrationResultDialog
                                registration={registration}
                                sportClassId={competitionClass.sport_class_id}
                                fetchClass={fetchClass}
                                round={level}
                                action="Lägg till resultat"
                            />
                        );
                    }

                    if (hasResult) {
                        return (
                            <AddRegistrationResultDialog
                                registration={registration}
                                sportClassId={competitionClass.sport_class_id}
                                fetchClass={fetchClass}
                                round={level}
                                action="Ändra resultat"
                            />
                        );
                    }

                    return <span className='text-muted-foreground'>Inga resultat ännu</span>;
                }
            }
        );
    }

    return baseColumns;
};


export function HeightLengthDatatable({ competitionClass, fetchClass, level, isLoading, setColumnVisibility, columnVisibility, onColumnsReady }) {
    const [sorting, setSorting] = useState(() => {
        if (competitionClass?.finished_at) {
            return [{ id: "placing", desc: false }];
        }
        return [{ id: "startNumber", desc: false }];
    });
    const [columnFilters, setColumnFilters] = useState([]);
    const { auth } = useContext(AuthContext);

    const formatAttempt = (attempts) => {
        // Check if attempts are null or invalid
        if (!attempts || !('attempt1' in attempts && 'attempt2' in attempts && 'attempt3' in attempts)) {
            return <span>Inga resultat ännu.</span>; // Placeholder for no results
        }

        // Check if all attempts are null
        if (attempts.attempt1 === null && attempts.attempt2 === null && attempts.attempt3 === null) {
            return <span>Inga resultat ännu.</span>; // Placeholder for all null results
        }

        const formatSingleAttempt = (attempt) => {
            if (attempt === true) return 'O';   // Pass
            if (attempt === false) return 'X';  // Fail
            return '';                         // No result or attempt needed
        };

        // Format individual attempts
        return (
            <>
                {formatSingleAttempt(attempts.attempt1)}
                {formatSingleAttempt(attempts.attempt2)}
                {formatSingleAttempt(attempts.attempt3)}
            </>
        );
    };

    const filteredRegistrations = useMemo(() => {
        return competitionClass.registrations.filter(registration => {
            const crossedOut = registration.crossedOut;

            if (crossedOut) return;

            const heightLengthResults = registration.heightLengthResults || [];

            // Extract all height lengths as numbers and sort them
            const sortedHeights = heightLengthResults
                .map(result => parseFloat(result.height_length))
                .sort((a, b) => a - b);

            // Find the highest height less than the current level
            const previousHeight = sortedHeights
                .filter(height => height < parseFloat(level))
                .pop(); // The highest height below the current level

            // Check if the bunny passed the previous height
            const passedPreviousHeight = heightLengthResults.some(result =>
                parseFloat(result.height_length) === previousHeight && (result.attempt1 === true || result.attempt2 === true || result.attempt3 === true)
            );

            // Check if the bunny has a result for the current height
            const hasCurrentLevelResult = heightLengthResults.some(result =>
                parseFloat(result.height_length) === parseFloat(level)
            );

            // Include bunnies that passed the previous height or have results for the current level
            return passedPreviousHeight || hasCurrentLevelResult || heightLengthResults.length === 0;
        }).map(registration => {
            const heightLengthResults = registration.heightLengthResults || [];

            // Check if the current level already has a placeholder
            const alreadyHasPlaceholder = heightLengthResults.some(
                result => parseFloat(result.height_length) === parseFloat(level)
            );

            // Add placeholder if no result exists for the current height
            const updatedResults = alreadyHasPlaceholder
                ? heightLengthResults
                : [
                    ...heightLengthResults,
                    {
                        height_length: `${parseFloat(level).toFixed(2)}`, // Ensure consistent string format
                        attempt1: null,
                        attempt2: null,
                        attempt3: null,
                    },
                ];

            return {
                ...registration,
                heightLengthResults: updatedResults,
            };
        });
    }, [competitionClass, level]);

    // Memoize the columns to prevent re-creation on every render
    const tableColumns = useMemo(() => {
        return columns(fetchClass, formatAttempt, competitionClass, level, auth, filteredRegistrations.length);
    }, [fetchClass, formatAttempt, competitionClass, level, auth, filteredRegistrations.length]);

    useEffect(() => {
        if (onColumnsReady) {
            onColumnsReady(tableColumns);
        }
    }, [onColumnsReady]);

    const table = useReactTable({
        data: filteredRegistrations,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            pagination: {
                pageIndex: 0,
                pageSize: 9999,
            },
        },
        meta: {
            getRowStyles: (row) => {
                // Check user permissions first
                const isPrivilegedUser = auth?.user?.superadmin ||
                    auth?.user?.bunnyadmin ||
                    auth?.user?.judge;

                // Check if class is finished
                const isClassFinished = competitionClass?.finished_at;

                // If user doesn't have permission or class is finished, don't apply styles
                if (!isPrivilegedUser || isClassFinished) {
                    return "";
                }

                // Otherwise, check if the row has a result for the current height/length
                const registration = row.original;
                const heightLengthResults = registration.heightLengthResults || [];

                // Find the result for the current level
                const currentLevelResult = heightLengthResults.find(
                    result => parseFloat(result.height_length) === parseFloat(level)
                );

                // Check if any attempt has a non-null value (true or false)
                const hasResult = currentLevelResult &&
                    (currentLevelResult.attempt1 !== null ||
                        currentLevelResult.attempt2 !== null ||
                        currentLevelResult.attempt3 !== null);

                return hasResult ? "!bg-skhrf-blue/10" : "";
            }
        }
    });

    if (filteredRegistrations.length === 0) {
        return (
            <p>
                Inga deltagare uppfyller kriterierna för omgång {level}.
            </p>
        );
    }

    return (
        <DataTable
            table={table}
            isLoading={isLoading}
            columns={tableColumns}
        />
    );
}

